// The Nativeorange offsite agenda, as supplied by the organisers.
//
// This is the seed an admin imports from the Itinerary page. Every block has a
// fixed `offsite-*` id so a re-import overwrites the same rows rather than
// duplicating them.
//
// Dates: the app-wide countdown is pinned to 2026-09-05 (see EventCountdown),
// and Sept 4 2026 is a Friday with Sept 5 a Saturday — which matches the
// "Day 1: Friday / Day 2: Saturday" framing of the source agenda.
//
// Saturday reconciliation: the source gave two versions of the same day — a
// top-level block list and a finer "Saturday breakdown". Per the organisers,
// the top-level times define the published blocks and the breakdown hangs
// inside them as sub-slots. Where a breakdown slot does not fit its parent
// cleanly, the discrepancy is recorded in `internalNote` (admin-only) rather
// than silently resolved.

export const OFFSITE_EVENT_NAME = 'Nativeorange Offsite';

const DAY_ONE = {
  day: 'Day 1',
  date: '2026-09-04',
  dayTheme: 'Icebreakers & High-Energy Party',
};

const DAY_TWO = {
  day: 'Day 2',
  date: '2026-09-05',
  dayTheme: 'Strategy Focus & Skyline Celebration',
};

export const OFFSITE_ITINERARY = [
  // ── Day 1 · Friday 4 September 2026 ────────────────────────────────────────
  {
    ...DAY_ONE,
    id: 'offsite-d1-lunch',
    title: 'Kickoff Corporate Lunch Buffet',
    type: 'meal',
    startTime: '13:30',
    endTime: '16:00',
    location: 'Chutney Chang',
    address: '31, HM Eleganza, Museum Road, near Church Street, Bengaluru',
    description: 'Everyone arrives and eats together — the official start of the offsite.',
    published: true,
  },
  {
    ...DAY_ONE,
    id: 'offsite-d1-teambuilding',
    title: 'Team-Building Activity',
    type: 'activity',
    startTime: '16:30',
    endTime: '19:30',
    location: 'BCU Basecamp by Push Sports',
    address: 'University campus, Palace Rd, Gandhi Nagar, Bengaluru, Karnataka 560009',
    published: true,
  },
  {
    ...DAY_ONE,
    id: 'offsite-d1-party',
    title: 'Dinner, Drinks & DJ Party',
    type: 'activity',
    startTime: '19:30',
    endTime: '',
    openEnded: true,
    location: 'Skyve — Night Owl Sky Lounge',
    address: '8/6, 3rd Floor, Alpine Kensington Point, Ulsoor Road, opposite the Conrad Hotel, Bengaluru, Karnataka 560008',
    description: 'Runs late — no fixed finish time.',
    published: true,
  },

  // ── Day 2 · Saturday 5 September 2026 ──────────────────────────────────────
  {
    ...DAY_TWO,
    id: 'offsite-d2-quarterly',
    title: 'Nativeorange Quarterly Meet',
    type: 'meeting',
    startTime: '09:00',
    endTime: '13:00',
    location: 'The Oberoi Bengaluru',
    conferenceRoom: 'In-house Conference Room',
    description: 'Quarterly strategy session for the whole team.',
    internalNote: 'Check with Ayush — get pictures. Breakdown has the working session starting 09:30, half an hour after this block opens.',
    agenda: [
      { id: 'd2q-intro',   startTime: '09:30', endTime: '10:00', title: 'Introduction' },
      { id: 'd2q-ice',     startTime: '10:00', endTime: '10:30', title: 'Icebreaker & games' },
      { id: 'd2q-leads',   startTime: '10:30', endTime: '11:30', title: 'Team strategy — leads presentations', owner: 'All leads', note: '10 minutes each' },
      { id: 'd2q-product', startTime: '11:30', endTime: '12:00', title: 'Product strategy', owner: 'Venkat' },
      { id: 'd2q-roadmap', startTime: '12:00', endTime: '12:30', title: 'Roadmap & strategy of Nativeorange' },
      { id: 'd2q-demo',    startTime: '12:30', endTime: '13:00', title: 'Product demo', owner: 'Srini + all leads', note: 'Driven by Srini, led by all the leads' },
    ],
    published: true,
  },
  {
    ...DAY_TWO,
    id: 'offsite-d2-lunch',
    title: 'Team Strategy Lunch',
    type: 'meal',
    startTime: '13:00',
    endTime: '15:00',
    location: 'The Oberoi Bengaluru',
    description: 'Hosted inside the hotel.',
    internalNote: 'Breakdown puts the founding engineers meet at 14:30–15:30, which overruns this block by 30 minutes.',
    agenda: [
      { id: 'd2l-lunch',    startTime: '13:00', endTime: '14:00', title: 'Lunch' },
      { id: 'd2l-founding', startTime: '14:30', endTime: '15:30', title: 'Founding engineers meet' },
    ],
    published: true,
  },
  {
    ...DAY_TWO,
    id: 'offsite-d2-leads',
    title: 'Leads Meet',
    type: 'meeting',
    startTime: '16:00',
    endTime: '17:00',
    internalNote: 'No venue given in the source. Breakdown has one-to-ones running 15:30–17:00, starting 30 minutes before this block.',
    agenda: [
      { id: 'd2lm-one-on-ones', startTime: '15:30', endTime: '17:00', title: 'One-to-ones with the team' },
    ],
    published: true,
  },
  {
    ...DAY_TWO,
    id: 'offsite-d2-amoeba',
    title: 'Activities & Dinner',
    type: 'activity',
    startTime: '19:00',
    endTime: '',
    openEnded: true,
    location: 'Amoeba Complex',
    description: 'Evening activities followed by dinner.',
    internalNote: 'Breakdown has this as 18:30–00:00; the top-level plan says 19:00 to late.',
    published: true,
  },
];

// Counts for the import confirmation, so the dialog never hardcodes a number.
export const OFFSITE_BLOCK_COUNT = OFFSITE_ITINERARY.length;
export const OFFSITE_SLOT_COUNT = OFFSITE_ITINERARY.reduce(
  (total, item) => total + (item.agenda ? item.agenda.length : 0),
  0
);
